import { ThemeEvents } from '@theme/events';

let syncingQuantity = false;
let syncingVariant = false;

const CHAT_OFFSET_IDLE = 20;
const CHAT_OFFSET_GAP = 12;

/** Cached sticky height used when the bar is fully settled. */
let cachedStickyHeight = 0;
/** @type {number | null} */
let chatSyncRaf = null;
/** Ignore MutationObserver callbacks caused by our own style writes. */
let ignoreChatMutationsUntil = 0;
/** Last bottom written to the CSS custom property. */
let lastPinnedBottom = -1;

/**
 * @returns {boolean}
 */
function isMobileStickyViewport() {
  return window.matchMedia('(max-width: 749px)').matches;
}

/**
 * Measure sticky ATC height only when fully stuck (not mid slide-in).
 * @param {HTMLElement} stickyPanel
 * @returns {number}
 */
function measureStickyHeight(stickyPanel) {
  const height = Math.ceil(stickyPanel.getBoundingClientRect().height);
  if (height > 0) cachedStickyHeight = height;
  return cachedStickyHeight || height;
}

/**
 * iOS visualViewport.offsetTop shifts fixed UI upward while scrolling.
 * Positive shift = translate the bubble back down so it stays glued above sticky ATC.
 * @returns {number}
 */
function getChatViewportShift() {
  const vv = window.visualViewport;
  if (!vv) return 0;
  return Math.round(vv.offsetTop || 0);
}

/**
 * Bottom offset that parks the bubble just above the sticky bar's live top edge.
 * @param {HTMLElement} stickyPanel
 * @returns {number}
 */
function getPinnedChatBottom(stickyPanel) {
  const rect = stickyPanel.getBoundingClientRect();
  if (rect.height > 0) {
    // Track the sticky bar's visual top so iOS fixed-position jitter cannot leave a gap.
    const fromStickyTop = Math.round(window.innerHeight - rect.top + CHAT_OFFSET_GAP);
    if (fromStickyTop > 0) return fromStickyTop;
  }

  const stickyHeight = cachedStickyHeight || measureStickyHeight(stickyPanel);
  return stickyHeight + CHAT_OFFSET_GAP;
}

/**
 * @returns {{ isStuck: boolean, offsetPx: number, shiftPx: number }}
 */
function getShopifyChatOffsetState() {
  const stickyPanel = document.querySelector('.sw-sticky-atc__panel');
  const isStuck =
    stickyPanel instanceof HTMLElement &&
    stickyPanel.dataset.stuck === 'true' &&
    isMobileStickyViewport();

  if (!isStuck) {
    return { isStuck: false, offsetPx: CHAT_OFFSET_IDLE, shiftPx: 0 };
  }

  return {
    isStuck: true,
    offsetPx: getPinnedChatBottom(stickyPanel),
    shiftPx: getChatViewportShift(),
  };
}

/**
 * Apply offset only when the inline value differs (avoids observer loops).
 * @param {HTMLElement} el
 * @param {number} offsetPx
 */
function applyShopifyChatOffset(el, offsetPx) {
  const desired = `${offsetPx}px`;
  const current = el.style.getPropertyValue('--shopify-agent-activator-offset').trim();
  if (current === desired) return;
  el.style.setProperty('--shopify-agent-activator-offset', desired, 'important');
}

/**
 * @param {HTMLElement} nudge
 * @param {number} offsetPx
 * @param {number} shiftPx
 */
function pinChatNudge(nudge, offsetPx, shiftPx) {
  const desiredTransform = shiftPx ? `translate3d(0, ${shiftPx}px, 0)` : 'none';

  // Always overwrite — Shopify/iOS rewrite these mid scroll-up and leave the bubble floating high.
  nudge.style.setProperty('bottom', `${offsetPx}px`, 'important');
  nudge.style.setProperty('top', 'auto', 'important');
  nudge.style.setProperty('position', 'fixed', 'important');
  nudge.style.setProperty('transform', desiredTransform, 'important');
}

/**
 * Host + inner shopify-agent-runtime both own the offset var.
 * Opening chat remounts the runtime with 20px, which overrides the host.
 * @param {HTMLElement} chat
 * @param {number} offsetPx
 * @param {number} shiftPx
 */
function applyShopifyChatOffsetTree(chat, offsetPx, shiftPx = 0) {
  applyShopifyChatOffset(chat, offsetPx);

  const root = chat.shadowRoot;
  if (!root) return;

  const runtimes = root.querySelectorAll('shopify-agent-runtime');
  for (const runtime of runtimes) {
    if (runtime instanceof HTMLElement) {
      applyShopifyChatOffset(runtime, offsetPx);
    }
  }

  const nudges = root.querySelectorAll('.activator-nudge');
  for (const nudge of nudges) {
    if (nudge instanceof HTMLElement) pinChatNudge(nudge, offsetPx, shiftPx);
  }

  for (const runtime of runtimes) {
    const nestedRoot = runtime instanceof HTMLElement ? runtime.shadowRoot : null;
    if (!nestedRoot) continue;
    for (const nudge of nestedRoot.querySelectorAll('.activator-nudge')) {
      if (nudge instanceof HTMLElement) pinChatNudge(nudge, offsetPx, shiftPx);
    }
  }

  // MutationObserver callbacks often run after this stack; ignore our own writes briefly.
  ignoreChatMutationsUntil = performance.now() + 64;
}

/**
 * Lift the Shopify chat bubble above the sticky ATC when the bar is visible.
 * @param {{ forceMeasure?: boolean }} [options]
 */
function syncShopifyChatOffset(options = {}) {
  const stickyPanel = document.querySelector('.sw-sticky-atc__panel');
  if (options.forceMeasure && stickyPanel instanceof HTMLElement && stickyPanel.dataset.stuck === 'true') {
    measureStickyHeight(stickyPanel);
  }

  const { isStuck, offsetPx, shiftPx } = getShopifyChatOffsetState();

  if (isStuck) {
    document.body.classList.add('sw-sticky-atc-visible');
    if (offsetPx !== lastPinnedBottom) {
      document.documentElement.style.setProperty('--sw-chat-sticky-offset', `${offsetPx}px`);
    }
  } else {
    document.body.classList.remove('sw-sticky-atc-visible');
    document.documentElement.style.removeProperty('--sw-chat-sticky-offset');
  }

  lastPinnedBottom = offsetPx;

  const chat = document.querySelector('shopify-chat');
  if (chat instanceof HTMLElement) {
    applyShopifyChatOffsetTree(chat, offsetPx, shiftPx);
  }
}

/**
 * Coalesce scroll / visualViewport updates onto one animation frame.
 */
function scheduleShopifyChatOffsetSync() {
  if (chatSyncRaf != null) return;
  chatSyncRaf = window.requestAnimationFrame(() => {
    chatSyncRaf = null;
    syncShopifyChatOffset();
  });
}

function setupShopifyChatOffsetSync() {
  const stickyPanel = document.querySelector('.sw-sticky-atc__panel');
  if (!(stickyPanel instanceof HTMLElement)) return;

  const stickyObserver = new MutationObserver(() => {
    lastPinnedBottom = -1;
    if (stickyPanel.dataset.stuck === 'true') {
      // Remeasure after the slide-in transition settles.
      window.setTimeout(() => syncShopifyChatOffset({ forceMeasure: true }), 320);
      syncShopifyChatOffset({ forceMeasure: true });
    } else {
      cachedStickyHeight = 0;
      syncShopifyChatOffset({ forceMeasure: true });
    }
  });
  stickyObserver.observe(stickyPanel, { attributes: true, attributeFilter: ['data-stuck'] });

  const stickyResizeObserver = new ResizeObserver(() => {
    if (stickyPanel.dataset.stuck === 'true') {
      measureStickyHeight(stickyPanel);
      lastPinnedBottom = -1;
      scheduleShopifyChatOffsetSync();
    }
  });
  stickyResizeObserver.observe(stickyPanel);

  window.addEventListener(
    'resize',
    () => {
      lastPinnedBottom = -1;
      syncShopifyChatOffset({ forceMeasure: true });
    },
    { passive: true }
  );

  // Keep the bubble glued while iOS moves the visual viewport on scroll-up/down.
  window.addEventListener('scroll', scheduleShopifyChatOffsetSync, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleShopifyChatOffsetSync, { passive: true });
    window.visualViewport.addEventListener('scroll', scheduleShopifyChatOffsetSync, { passive: true });
  }

  /** @type {HTMLElement | null} */
  let watchedChat = null;
  /** @type {MutationObserver | null} */
  let chatAttrObserver = null;
  /** @type {MutationObserver | null} */
  let chatShadowObserver = null;
  /** @type {number | null} */
  let resyncTimer = null;

  function scheduleResyncBurst() {
    if (performance.now() < ignoreChatMutationsUntil) return;
    if (resyncTimer != null) window.clearTimeout(resyncTimer);
    // Shopify remounts agent-runtime shortly after open/close; re-apply a few times.
    let ticks = 0;
    const run = () => {
      lastPinnedBottom = -1;
      syncShopifyChatOffset({ forceMeasure: true });
      ticks += 1;
      if (ticks < 8) {
        resyncTimer = window.setTimeout(run, 50);
      } else {
        resyncTimer = null;
      }
    };
    run();
  }

  /**
   * @param {HTMLElement} chat
   */
  function watchShopifyChat(chat) {
    if (watchedChat === chat && chatAttrObserver && chatShadowObserver) {
      syncShopifyChatOffset({ forceMeasure: true });
      return;
    }

    watchedChat = chat;
    chatAttrObserver?.disconnect();
    chatShadowObserver?.disconnect();

    chatAttrObserver = new MutationObserver(scheduleResyncBurst);
    chatAttrObserver.observe(chat, {
      attributes: true,
      attributeFilter: ['style', 'view', 'shell-ready'],
    });

    const root = chat.shadowRoot;
    if (root) {
      chatShadowObserver = new MutationObserver(scheduleResyncBurst);
      chatShadowObserver.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'view', 'shell-ready', 'open'],
      });
    }

    syncShopifyChatOffset({ forceMeasure: true });
  }

  const existingChat = document.querySelector('shopify-chat');
  if (existingChat instanceof HTMLElement) {
    watchShopifyChat(existingChat);
  }

  const chatMountObserver = new MutationObserver(() => {
    const chat = document.querySelector('shopify-chat');
    if (chat instanceof HTMLElement) {
      watchShopifyChat(chat);
    }
  });
  chatMountObserver.observe(document.documentElement, { childList: true, subtree: true });

  syncShopifyChatOffset({ forceMeasure: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupShopifyChatOffsetSync, { once: true });
} else {
  setupShopifyChatOffsetSync();
}

/**
 * @param {string} productId
 * @returns {HTMLElement | null}
 */
function getMainQuantitySelector(productId) {
  const sticky = document.querySelector(`sticky-add-to-cart.sw-sticky-atc[data-product-id="${productId}"]`);
  const section = sticky?.closest('.shopify-section');
  if (!section) return null;

  const sectionId = section.id.replace('shopify-section-', '');
  const productForm = document.querySelector(
    `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
  );

  return productForm?.querySelector('quantity-selector-component') ?? null;
}

/**
 * @param {string} productId
 * @returns {HTMLElement | null}
 */
function getMainVariantPicker(productId) {
  return document.querySelector(`variant-picker[data-product-id="${productId}"]`);
}

/**
 * @param {HTMLElement | null} component
 * @param {number} quantity
 */
function syncQuantityComponent(component, quantity) {
  if (!component) return;

  const input = component.querySelector('input[ref="quantityInput"]');
  if (!input || parseInt(input.value, 10) === quantity) return;

  input.value = quantity.toString();

  if ('updateButtonStates' in component && typeof component.updateButtonStates === 'function') {
    component.updateButtonStates();
  }
}

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  if (!(event instanceof CustomEvent) || event.detail?.cartLine) return;
  if (syncingQuantity) return;
  if (window.matchMedia('(min-width: 750px)').matches) return;

  const sticky = document.querySelector('sticky-add-to-cart.sw-sticky-atc');
  if (!sticky) return;

  const productId = sticky.dataset.productId;
  if (!productId) return;

  const stickyQty = sticky.querySelector('quantity-selector-component');
  const mainQty = getMainQuantitySelector(productId);
  const quantity = event.detail.quantity;
  const fromSticky = event.target instanceof Element && Boolean(event.target.closest('.sw-sticky-atc'));

  syncingQuantity = true;

  if (fromSticky) {
    syncQuantityComponent(mainQty, quantity);
  } else {
    syncQuantityComponent(stickyQty, quantity);
  }

  syncingQuantity = false;
});

/**
 * @param {HTMLInputElement} stickyInput
 */
function syncStickyVariantToMain(stickyInput) {
  if (syncingVariant) return;

  const sticky = stickyInput.closest('sticky-add-to-cart.sw-sticky-atc');
  const productId = sticky?.dataset.productId;
  const optionValueId = stickyInput.dataset.optionValueId;

  if (!productId || !optionValueId) return;

  const mainInput = getMainVariantPicker(productId)?.querySelector(
    `input[data-option-value-id="${optionValueId}"]`
  );

  if (!(mainInput instanceof HTMLInputElement) || mainInput.checked) return;

  syncingVariant = true;
  mainInput.checked = true;
  mainInput.dataset.currentChecked = 'true';
  mainInput.dispatchEvent(new Event('change', { bubbles: true }));
  syncingVariant = false;
}

/**
 * @param {string} productId
 */
function syncMainVariantToSticky(productId) {
  if (syncingVariant) return;

  const sticky = document.querySelector(`sticky-add-to-cart.sw-sticky-atc[data-product-id="${productId}"]`);
  const mainPicker = getMainVariantPicker(productId);
  if (!sticky || !mainPicker) return;

  const checkedMainInput = mainPicker.querySelector('input[data-option-value-id]:checked');
  if (!(checkedMainInput instanceof HTMLInputElement)) return;

  const stickyInput = sticky.querySelector(
    `input[data-sticky-variant-input][data-option-value-id="${checkedMainInput.dataset.optionValueId}"]`
  );

  if (!(stickyInput instanceof HTMLInputElement) || stickyInput.checked) return;

  syncingVariant = true;
  stickyInput.checked = true;
  stickyInput.dataset.currentChecked = 'true';
  syncingVariant = false;
}

document.addEventListener('change', (event) => {
  if (window.matchMedia('(min-width: 750px)').matches) return;
  if (!(event.target instanceof HTMLInputElement)) return;
  if (!event.target.matches('[data-sticky-variant-input]')) return;

  syncStickyVariantToMain(event.target);
});

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  if (window.matchMedia('(min-width: 750px)').matches) return;
  if (!(event instanceof CustomEvent)) return;

  const productId = event.detail?.data?.productId;
  if (!productId) return;

  const featuredMedia = event.detail?.resource?.featured_media;
  if (featuredMedia?.preview_image?.src) {
    const stickyImage = document.querySelector(
      `sticky-add-to-cart.sw-sticky-atc[data-product-id="${productId}"] [ref="productImage"]`
    );
    if (stickyImage instanceof HTMLImageElement && stickyImage.src !== featuredMedia.preview_image.src) {
      stickyImage.src = featuredMedia.preview_image.src;
      if (featuredMedia.alt) stickyImage.alt = featuredMedia.alt;
    }
  }

  requestAnimationFrame(() => {
    syncMainVariantToSticky(String(productId));
  });
});
